import { useMemo, useState } from 'react';

import { AudioLinesIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTitle } from 'react-use';

import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { DataTable } from '@/components/ui/table/data-table';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';

import { createColumns } from './columns';
import { DeleteSpeechModelModal } from './DeleteSpeechModelModal';
import { SpeechModel } from '../../types';

interface SpeechModelsProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
  onOpenForm: (speechModel?: SpeechModel) => void;
}

export const SpeechModels = ({ onEmptyStateChange, onOpenForm }: SpeechModelsProps) => {
  const { t } = useTranslation();
  const {
    speechModels,
    listStatus,
    refetchList,
    providersStatus,
    refetchProviders,
    canWrite,
    canDelete,
  } = useSpeechModels();
  const [deleteTarget, setDeleteTarget] = useState<SpeechModel | null>(null);

  useTitle(t('pages.devtool_speech_models'));

  const columns = useMemo(
    () => createColumns({ onEdit: onOpenForm, onDelete: setDeleteTarget, canWrite, canDelete }),
    [onOpenForm, canWrite, canDelete]
  );

  const isLoading = listStatus === 'pending';
  // Create needs the provider catalog, so its failure blocks the page like the list's
  const isError = listStatus === 'error' || providersStatus === 'error';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'speech-models',
    isLoading,
    isEmpty: !speechModels.length,
    isError,
  });

  // An empty page hides the header's Create button, so a failed load cannot offer it either
  useReportEmptyState(isEmptyPage || isError, onEmptyStateChange);

  if (isError) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3"
      >
        <span className="text-sm leading-5 text-destructive">
          {t('developer_tools.speech_models.load_failed')}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (listStatus === 'error') {
              refetchList();
            }
            if (providersStatus === 'error') {
              refetchProviders();
            }
          }}
        >
          {t('common.try_again')}
        </Button>
      </div>
    );
  }

  return (
    <>
      {showEmpty && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <AudioLinesIcon />
            </EmptyMedia>
            <EmptyTitle>{t('developer_tools.speech_models.empty_title')}</EmptyTitle>
            <EmptyDescription>
              {t('developer_tools.speech_models.empty_description')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button size="sm" onClick={() => onOpenForm()} disabled={!canWrite}>
              <PlusIcon size={16} />
              {t('developer_tools.speech_models.create_title')}
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={2} />}

      {showList && (
        <DataTable.Root
          fill
          className="min-h-0 flex-1"
          columns={columns}
          data={speechModels}
          rowCount={speechModels.length}
          isLoading={isLoading}
          resourceName="speech-models"
          emptyStateMessage={t('developer_tools.speech_models.no_results')}
          getRowId={(row) => row.speech_model_id}
          onRowClick={canWrite ? onOpenForm : undefined}
          enableRowHover
        >
          <DataTable.Content />
          <DataTable.Pagination />
        </DataTable.Root>
      )}

      {deleteTarget && (
        <DeleteSpeechModelModal speechModel={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
    </>
  );
};
